import React, { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend } from "recharts";
import LessonLayout from "./LessonLayout";

const fmt = (n) => `$${Math.round(n).toLocaleString()}`;
const fmtShort = (n) => (n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `$${Math.round(n / 1_000)}k` : `$${Math.round(n)}`);

const grow = ({ monthly, years, rate }) => {
  const monthlyRate = rate / 12;
  let bal = 0;
  const points = [{ year: 0, balance: 0 }];
  for (let y = 1; y <= years; y++) {
    for (let m = 0; m < 12; m++) bal = bal * (1 + monthlyRate) + monthly;
    points.push({ year: y, balance: bal });
  }
  return { final: bal, points };
};

export default function LearnFirstRoth() {
  const [monthly, setMonthly] = useState(300);
  const [taxRate, setTaxRate] = useState(24);
  const years = 40;

  const roth = useMemo(() => grow({ monthly, years, rate: 0.07 }), [monthly]);
  const taxable = useMemo(() => grow({ monthly, years, rate: 0.07 * (1 - taxRate / 100) }), [monthly, taxRate]);

  // Match on same X axis
  const merged = roth.points.map((r, i) => ({
    year: r.year,
    roth: r.balance,
    taxable_after_tax: taxable.points[i]?.balance,
  }));
  const savedTaxes = roth.final - taxable.final;

  return (
    <LessonLayout
      meta={{
        number: 3,
        slug: "first-roth",
        time: "6 min read",
        title: "Your first Roth IRA — basically free money.",
        hook: "The Roth IRA is the closest thing to a legal cheat code in personal finance. If you're under 30 and not maxing it, you're leaving 6 figures on the table.",
        quickAnswer:
          "A Roth IRA is a retirement account where you contribute after-tax money and every dollar of growth comes out tax-free after age 59½. Anyone with earned income can contribute (subject to IRS income limits), contributions can be withdrawn anytime without penalty, and decades of tax-free compounding make it the best first investing account for most people.",
        seoDescription: "Interactive lesson on the Roth IRA — the tax-free growth account that beats a taxable brokerage account by 6 figures over a lifetime.",
        bookCategory: "wealth",
      }}
      next={{ slug: "four-percent", title: "The 4% rule (and why it's wrong)" }}
    >
      <div className="bg-white border border-[#E5E2DC] rounded-2xl p-6">
        <div className="flex flex-wrap gap-5 mb-6">
          <div>
            <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold">
              Monthly contribution
            </Label>
            <div className="mt-1.5 flex items-center bg-[#F9F8F6] border border-[#E5E2DC] rounded-lg w-fit">
              <span className="pl-3 text-[#5C6B62] text-sm">$</span>
              <Input
                data-testid="learn-roth-monthly"
                type="number"
                value={monthly}
                onChange={(e) => setMonthly(Number(e.target.value) || 0)}
                className="border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 font-mono-num text-lg py-2 w-28"
              />
            </div>
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold">
              Your tax rate on investment gains
            </Label>
            <div className="mt-1.5 flex items-center bg-[#F9F8F6] border border-[#E5E2DC] rounded-lg w-fit">
              <Input
                type="number"
                value={taxRate}
                onChange={(e) => setTaxRate(Number(e.target.value) || 0)}
                className="border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 font-mono-num text-lg py-2 w-20"
              />
              <span className="pr-3 text-[#5C6B62] text-sm">%</span>
            </div>
          </div>
        </div>

        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={merged} margin={{ top: 10, right: 30, left: 20, bottom: 10 }}>
            <XAxis dataKey="year" tick={{ fontSize: 11, fill: "#5C6B62" }} axisLine={{ stroke: "#E5E2DC" }} unit=" yr" />
            <YAxis tickFormatter={fmtShort} tick={{ fontSize: 11, fill: "#5C6B62" }} axisLine={{ stroke: "#E5E2DC" }} />
            <Tooltip formatter={(v) => fmt(v)} labelFormatter={(y) => `Year ${y}`} contentStyle={{ backgroundColor: "#fff", border: "1px solid #E5E2DC", borderRadius: 8 }} />
            <Legend />
            <Line type="monotone" dataKey="roth" stroke="#2B5339" strokeWidth={3} dot={false} name="Roth IRA (tax-free growth)" />
            <Line type="monotone" dataKey="taxable_after_tax" stroke="#C26747" strokeWidth={2} strokeDasharray="6 4" dot={false} name="Regular brokerage (taxed on gains)" />
          </LineChart>
        </ResponsiveContainer>

        <div className="mt-5 bg-[#E8F0EB] border border-[#2B5339] rounded-xl p-5">
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#2B5339] font-semibold mb-1">
            Money the Roth saves you over 40 years
          </div>
          <div className="font-mono-num text-3xl text-[#2B5339]" data-testid="learn-roth-diff">{fmt(savedTaxes)}</div>
          <div className="text-xs text-[#1C2B23] mt-2 leading-relaxed">
            Same contributions, same market returns — but the Roth pays no tax on the growth.
          </div>
        </div>
      </div>

      <div className="prose prose-sm max-w-none text-[#1C2B23] space-y-4 leading-relaxed">
        <h2 className="text-2xl font-medium">What a Roth IRA is, in one sentence</h2>
        <p>
          You put in <strong>already-taxed money</strong>, and everything it grows into is <strong>never taxed again</strong>.
          Withdraw it in retirement, tax-free.
        </p>

        <h2 className="text-2xl font-medium mt-8">The rules (as of 2026)</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li>Max contribution: <strong>$7,000/year</strong> ($8,000 if you&apos;re over 50).</li>
          <li>You must have <strong>earned income</strong> (from a job) at least equal to what you contribute.</li>
          <li>If you make over ~$165k single / ~$246k married, you&apos;re phased out. Below that, you&apos;re fully eligible.</li>
          <li>Money must stay in until age 59½ for tax-free growth. But you can <strong>withdraw your contributions (not gains) anytime, tax-free, no penalty</strong>. That&apos;s why it works as a backup emergency fund.</li>
        </ul>

        <h2 className="text-2xl font-medium mt-8">How to open one (~15 minutes)</h2>
        <ol className="list-decimal pl-6 space-y-2">
          <li>Go to <strong>Fidelity</strong>, <strong>Vanguard</strong>, or <strong>Schwab</strong> (any of the three — they&apos;re all free and reputable).</li>
          <li>Click &ldquo;Open an account&rdquo; → &ldquo;Roth IRA&rdquo;.</li>
          <li>Fill in your info. You&apos;ll need your SSN.</li>
          <li>Fund it from your checking account (they&apos;ll ask for routing/account numbers).</li>
          <li>Buy one of these index funds: FZROX (Fidelity), VTI (Vanguard), or SWTSX (Schwab). Any of them = &ldquo;the whole US stock market&rdquo;.</li>
        </ol>

        <h2 className="text-2xl font-medium mt-8">The most important part</h2>
        <p className="text-[#2B5339] font-medium">
          Set an automatic monthly contribution from checking to the Roth. Even $50/mo. You&apos;ll never miss
          it — and 40 years from now, that automation will have made you a millionaire without a single active decision.
        </p>
      </div>
    </LessonLayout>
  );
}
